import { type CSSProperties } from 'react';
import { Badge, type BadgeProps } from './Badge';

export interface StatusPillProps extends Omit<BadgeProps, 'children'> {
  /**
   * Raw BE-supplied displayStatus value. Exposed as `data-status` for e2e
   * selectors and Sentry breadcrumbs. UNKNOWN sentinel when missing
   * (architect C2 + frontend/CLAUDE.md displayStatus rule).
   */
  displayStatus: string | null | undefined;
  /**
   * Resolved presentation label (already translated). i18n lookup is done by
   * the domain wrapper so the primitive stays framework-agnostic.
   */
  label: string;
  /**
   * Stable testid for domain-specific selectors (e.g. `order-status-pill`).
   * Becomes the `data-testid` attribute. Required so e2e can target the pill
   * without falling back to text matching.
   */
  testId: string;
  /**
   * Optional native `title` for hover tooltip (e.g. last-transition timestamp).
   */
  tooltip?: string;
}

export const StatusPill = ({
  displayStatus,
  label,
  testId,
  tooltip,
  variant,
  style,
  ...rest
}: StatusPillProps) => {
  const a11yStyle: CSSProperties | undefined = style;
  return (
    <Badge
      variant={variant}
      data-testid={testId}
      data-badge={variant ?? 'neutral'}
      data-status={displayStatus ?? 'UNKNOWN'}
      title={tooltip}
      style={a11yStyle}
      {...rest}
    >
      {label}
    </Badge>
  );
};
